import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ScopingAILayout from '../../components/RevisrLayout';
import { Stepper } from '@foundation/components/stepper';
import { Button } from '@foundation/components/ui/button';
import { Badge } from '@foundation/components/ui/badge';
import { useUser } from '@foundation/contextapi/UserContext';
import { useWorkspaceContext } from '@foundation/hooks/useWorkspaceContext';
import { useToast } from '@foundation/hooks/use-toast';
import BrandingService from '@foundation/services/brandingService';
import { EditorLayout, EditorActionButtons } from '../../components/editor/shared/EditorLayout';
import { SortableSectionList } from '../../components/editor/shared/SortableSectionList';
import SectionRegenerateDialog from '../../components/create-policy/SectionRegenerateDialog';
import YooptaMarkdownEditor from '../../components/editor/YooptaMarkdownEditor';
import { saveContent, updateStep, regenerateSection } from '../../services/policyGenerationService';
import { supabaseRevisr } from '../../utils/supabaseRevisrClient';
import { splitMarkdownIntoSections, mergeSectionsToMarkdown } from '../../utils/sectionUtils';
import type { PolicySection } from '../../types/policyGeneration';
import {
  ArrowLeft,
  ArrowRight,
  FileText,
  Loader2,
  CheckCircle,
  Sparkles,
} from 'lucide-react';

const CREATE_POLICY_STEPS = [
  { label: 'Details', description: 'Enter policy info' },
  { label: 'Generate', description: 'AI generates draft' },
  { label: 'Edit', description: 'Review & refine' },
  { label: 'Export', description: 'Preview & export' },
];

// Internal section type with string ID for SortableSectionList
type EditorSection = {
  id: string;
  title: string;
  content: string;
  edited?: boolean;
};

function policySectionsToEditorSections(sections: PolicySection[]): EditorSection[] {
  return sections.map((s, i) => ({
    id: `section-${i}`,
    title: s.title,
    content: s.content,
    edited: s.isEdited,
  }));
}

function editorSectionsToMarkdown(sections: EditorSection[]): string {
  return sections
    .map((s) => `## ${s.title}\n\n${s.content}`)
    .join('\n\n');
}

export default function PolicyEditPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useUser();
  const { currentWorkspace } = useWorkspaceContext();
  const { toast } = useToast();

  const orgId = currentWorkspace?.id || '';
  const userId = user?.id || '';

  // URL-driven ID (primary), sessionStorage (fallback)
  const urlPolicyId = searchParams.get('id') || '';
  const sessionPolicyId = sessionStorage.getItem('create_policy_policy_review_id') || '';
  const policyReviewId = urlPolicyId || sessionPolicyId;

  const [policyName, setPolicyName] = useState(sessionStorage.getItem('create_policy_policy_name') || '');

  // Section state
  const [sections, setSections] = useState<EditorSection[]>([]);
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'edit' | 'section-preview' | 'document-preview'>('edit');
  const [editorKey, setEditorKey] = useState(0);

  // Save state
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Regeneration state
  const [isRegenerateDialogOpen, setIsRegenerateDialogOpen] = useState(false);
  const [regeneratingSectionId, setRegeneratingSectionId] = useState<string | null>(null);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Branding state
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [fontFamily, setFontFamily] = useState<string>('Inter, system-ui, sans-serif');
  const [primaryColor, setPrimaryColor] = useState<string>('#1a365d');
  const [secondaryColor, setSecondaryColor] = useState<string>('#374151');
  const [defaultColor, setDefaultColor] = useState<string>('#333333');

  const hasUpdatedStepRef = useRef(false);

  // Sync URL ID to sessionStorage
  useEffect(() => {
    if (urlPolicyId) {
      sessionStorage.setItem('create_policy_policy_review_id', urlPolicyId);
    }
  }, [urlPolicyId]);

  // Update step to 3 and status to 'finalised' on mount
  useEffect(() => {
    if (!policyReviewId || !userId || !orgId || hasUpdatedStepRef.current) return;
    hasUpdatedStepRef.current = true;

    updateStep(policyReviewId, 3, userId, orgId, 'finalised').catch((err) => {
      console.error('Error updating step to 3:', err);
    });
  }, [policyReviewId, userId, orgId]);

  // Load content: try sessionStorage first, then fall back to DB
  useEffect(() => {
    let isMounted = true;

    const loadContent = async () => {
      // Try sessionStorage first
      const generatedContent = sessionStorage.getItem('create_policy_generated_content');
      if (generatedContent) {
        const parsed = splitMarkdownIntoSections(generatedContent);
        const editorSections = policySectionsToEditorSections(parsed);
        if (isMounted) {
          setSections(editorSections);
          if (editorSections.length > 0) {
            setSelectedSectionId(editorSections[0].id);
          }
        }
        return;
      }

      // Fall back to DB: fetch markdown_url, download from storage
      if (!policyReviewId) return;

      try {
        const { data, error: fetchError } = await supabaseRevisr
          .from('revisr_final_policies')
          .select('markdown_url, policy_name')
          .eq('id', policyReviewId)
          .single();

        if (fetchError || !data) {
          console.error('Error loading policy from DB:', fetchError);
          return;
        }

        if (data.policy_name && isMounted) {
          setPolicyName(data.policy_name);
          sessionStorage.setItem('create_policy_policy_name', data.policy_name);
        }

        if (data.markdown_url) {
          const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
          const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

          const contentResponse = await fetch(
            `${supabaseUrl}/storage/v1/object/public/knowledge_docs/${data.markdown_url}`,
            {
              headers: {
                'apikey': supabaseKey,
                'Authorization': `Bearer ${supabaseKey}`,
              },
            }
          );

          if (contentResponse.ok) {
            const markdownContent = await contentResponse.text();
            sessionStorage.setItem('create_policy_generated_content', markdownContent);

            if (isMounted) {
              const parsed = splitMarkdownIntoSections(markdownContent);
              const editorSections = policySectionsToEditorSections(parsed);
              setSections(editorSections);
              if (editorSections.length > 0) {
                setSelectedSectionId(editorSections[0].id);
              }
            }
          }
        }
      } catch (err) {
        console.error('Error loading content from DB:', err);
      }
    };

    loadContent();
    return () => { isMounted = false; };
  }, [policyReviewId]);

  // Fetch organization branding - only once
  useEffect(() => {
    if (!orgId) return;

    let isMounted = true;

    const fetchBranding = async () => {
      try {
        const branding = await BrandingService.getOrganizationBranding(orgId);
        if (branding && isMounted) {
          if (branding.logo_url) {
            setLogoPreview(BrandingService.getLogoUrlWithCacheBusting(branding.logo_url));
          }
          if (branding.font_family) setFontFamily(branding.font_family);
          if (branding.primary_color) setPrimaryColor(branding.primary_color);
          if (branding.secondary_color) setSecondaryColor(branding.secondary_color);
          if (branding.font_color) setDefaultColor(branding.font_color);
        }
      } catch (err) {
        console.error('Error fetching branding:', err);
      }
    };

    fetchBranding();

    return () => {
      isMounted = false;
    };
  }, [orgId]); // Only depend on orgId, fetch once per org

  // Cleanup: abort any ongoing regeneration on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        console.log('🛑 Component unmounting - aborting ongoing regeneration');
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, []);

  const selectedSection = sections.find((s) => s.id === selectedSectionId) || null;
  const selectedSectionIndex = sections.findIndex((s) => s.id === selectedSectionId);

  // Auto-save with 2s debounce
  const triggerAutoSave = useCallback((updatedSections: EditorSection[]) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    setSaveStatus('saving');
    saveTimeoutRef.current = setTimeout(() => {
      const fullContent = editorSectionsToMarkdown(updatedSections);
      sessionStorage.setItem('create_policy_generated_content', fullContent);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }, 2000);
  }, []);

  // Handle section content change from YooptaMarkdownEditor
  const handleSectionContentChange = useCallback((newContent: string) => {
    setSections((prev) => {
      const updated = prev.map((s) =>
        s.id === selectedSectionId
          ? { ...s, content: newContent, edited: true }
          : s
      );
      triggerAutoSave(updated);
      return updated;
    });
  }, [selectedSectionId, triggerAutoSave]);

  // SortableSectionList callbacks
  const handleSectionSelect = useCallback((sectionId: string) => {
    setSelectedSectionId(sectionId);
    setActiveTab('edit');
    setEditorKey((k) => k + 1);
  }, []);

  const handleSectionReorder = useCallback((reordered: EditorSection[]) => {
    setSections((prevSections) => {
      const updated = reordered.map((s, i) => ({ ...s, id: `section-${i}` }));
      // Track selected section through the reorder
      if (selectedSectionId) {
        const originalIndex = prevSections.findIndex((s) => s.id === selectedSectionId);
        if (originalIndex !== -1) {
          const originalTitle = prevSections[originalIndex].title;
          const reorderedIndex = reordered.findIndex((s) => s.title === originalTitle);
          if (reorderedIndex !== -1) {
            setSelectedSectionId(`section-${reorderedIndex}`);
            setEditorKey((k) => k + 1);
          }
        }
      }
      triggerAutoSave(updated);
      return updated;
    });
  }, [selectedSectionId, triggerAutoSave]);

  const handleSectionTitleChange = useCallback((sectionId: string, newTitle: string) => {
    setSections((prev) => {
      const updated = prev.map((s) =>
        s.id === sectionId ? { ...s, title: newTitle, edited: true } : s
      );
      triggerAutoSave(updated);
      return updated;
    });
  }, [triggerAutoSave]);

  const handleSectionDelete = useCallback((sectionId: string) => {
    if (sections.length <= 1) {
      toast({
        title: 'Cannot delete',
        description: 'You must have at least one section.',
        variant: 'destructive',
      });
      return;
    }

    setSections((prev) => {
      const updated = prev.filter((s) => s.id !== sectionId).map((s, i) => ({ ...s, id: `section-${i}` }));
      if (selectedSectionId === sectionId) {
        setSelectedSectionId(updated.length > 0 ? updated[0].id : null);
        setEditorKey((k) => k + 1);
      }
      triggerAutoSave(updated);
      return updated;
    });
  }, [sections.length, selectedSectionId, triggerAutoSave, toast]);

  const handleSectionAdd = useCallback(() => {
    setSections((prev) => {
      const newId = `section-${prev.length}`;
      const updated = [...prev, { id: newId, title: 'New Section', content: '', edited: true }];
      setSelectedSectionId(newId);
      setActiveTab('edit');
      setEditorKey((k) => k + 1);
      triggerAutoSave(updated);
      return updated;
    });
  }, [triggerAutoSave]);

  const handleSectionAddAfter = useCallback((afterSectionId: string) => {
    setSections((prev) => {
      const idx = prev.findIndex((s) => s.id === afterSectionId);
      if (idx === -1) return prev;
      const newSection: EditorSection = { id: 'temp', title: 'New Section', content: '', edited: true };
      const updated = [...prev.slice(0, idx + 1), newSection, ...prev.slice(idx + 1)]
        .map((s, i) => ({ ...s, id: `section-${i}` }));
      const newId = `section-${idx + 1}`;
      setSelectedSectionId(newId);
      setActiveTab('edit');
      setEditorKey((k) => k + 1);
      triggerAutoSave(updated);
      return updated;
    });
  }, [triggerAutoSave]);

  const handleSectionEdit = useCallback((sectionId: string) => {
    setSelectedSectionId(sectionId);
    setActiveTab('edit');
    setEditorKey((k) => k + 1);
  }, []);

  // Regenerate section
  const handleOpenRegenDialog = () => {
    if (!selectedSectionId) return;
    setRegeneratingSectionId(selectedSectionId);
    setIsRegenerateDialogOpen(true);
  };

  // Helper function to strip heading from regenerated content
  const stripHeadingFromContent = (content: string): string => {
    if (!content || !content.trim()) {
      return '';
    }

    const lines = content.split('\n');

    // Remove first line if it's a heading (starts with ## or more #'s)
    if (lines[0] && lines[0].trim().match(/^#{2,}\s+/)) {
      // Remove the heading line and any leading empty lines after it
      const contentLines = lines.slice(1);
      let startIndex = 0;

      // Skip empty lines after heading
      while (startIndex < contentLines.length && contentLines[startIndex].trim() === '') {
        startIndex++;
      }

      return contentLines.slice(startIndex).join('\n').trim();
    }

    return content.trim();
  };

  const handleRegenerate = async (prompt: string) => {
    if (!regeneratingSectionId) return;

    const section = sections.find((s) => s.id === regeneratingSectionId);
    if (!section) return;

    const sectionIdx = sections.findIndex((s) => s.id === regeneratingSectionId);

    // Create a new AbortController for this request
    abortControllerRef.current = new AbortController();

    try {
      setIsRegenerating(true);

      console.log('🔄 Starting section regeneration:', {
        sectionTitle: section.title,
        sectionIndex: sectionIdx,
        hasPrompt: !!prompt,
      });

      // Call regenerate API and wait for complete response
      const regenerated = await regenerateSection(
        policyReviewId,
        {
          sectionIndex: sectionIdx,
          sectionTitle: section.title,
          additionalPrompt: prompt,
          fullDocumentContext: editorSectionsToMarkdown(sections),
        },
        userId,
        orgId,
        abortControllerRef.current.signal
      );

      if (regenerated.action === 'remove') {
        // The prompt asked to delete this section — drop it from the document.
        setSections((prev) => {
          const updated = prev.filter((s) => s.id !== regeneratingSectionId);
          triggerAutoSave(updated);
          return updated;
        });

        setIsRegenerateDialogOpen(false);
        setEditorKey((k) => k + 1);

        toast({
          title: 'Section removed',
          description: `"${section.title}" has been removed from the document.`,
        });
        return;
      }

      console.log('✅ Regeneration complete, content length:', regenerated.content.length);

      // Strip the heading from the regenerated content
      const cleanedContent = stripHeadingFromContent(regenerated.content);

      if (!cleanedContent) {
        throw new Error('Regenerated content is empty');
      }

      setSections((prev) => {
        const updated = prev.map((s) =>
          s.id === regeneratingSectionId
            ? { ...s, content: cleanedContent, edited: true }
            : s
        );
        triggerAutoSave(updated);
        return updated;
      });

      setIsRegenerateDialogOpen(false);
      setEditorKey((k) => k + 1);

      toast({
        title: 'Section regenerated',
        description: `"${section.title}" has been regenerated successfully.`,
      });
    } catch (err) {
      // Don't show error for intentional aborts
      if (err instanceof Error && err.name === 'AbortError') {
        console.log('⚠️ Regeneration was cancelled');
        return;
      }

      console.error('Error regenerating section:', err);
      toast({
        title: 'Regeneration failed',
        description: err instanceof Error ? err.message : 'Failed to regenerate section.',
        variant: 'destructive',
      });
    } finally {
      setIsRegenerating(false);
      abortControllerRef.current = null; // Clean up
    }
  };

  // Continue to export
  const handleContinueToExport = async () => {
    try {
      setIsSaving(true);

      const fullContent = editorSectionsToMarkdown(sections);

      await saveContent(policyReviewId, fullContent, userId, orgId);
      sessionStorage.setItem('create_policy_generated_content', fullContent);

      navigate(`/revisr/create-policy/export?id=${policyReviewId}`);
    } catch (err) {
      console.error('Error saving content:', err);
      toast({
        title: 'Error',
        description: 'Failed to save content. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Branded markdown components for preview - consistent font sizes matching editor
  const brandedMarkdownComponents = {
    h1: ({ children }: { children: React.ReactNode }) => (
      <h1 style={{ color: primaryColor, fontFamily, fontSize: '2rem', fontWeight: 700, marginBottom: '1rem', marginTop: '1.5rem', lineHeight: 1.3, borderBottom: `2px solid ${primaryColor}20`, paddingBottom: '0.5rem' }}>{children}</h1>
    ),
    h2: ({ children }: { children: React.ReactNode }) => (
      <h2 style={{ color: secondaryColor, fontFamily, fontSize: '1.5rem', fontWeight: 600, marginBottom: '0.75rem', marginTop: '1.5rem', lineHeight: 1.3, borderBottom: `2px solid ${secondaryColor}15`, paddingBottom: '0.4rem' }}>{children}</h2>
    ),
    h3: ({ children }: { children: React.ReactNode }) => (
      <h3 style={{ color: secondaryColor, fontFamily, fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.5rem', marginTop: '1.25rem', lineHeight: 1.3 }}>{children}</h3>
    ),
    h4: ({ children }: { children: React.ReactNode }) => (
      <h4 style={{ color: secondaryColor, fontFamily, fontSize: '1.1rem', fontWeight: 600, marginBottom: '0.4rem', marginTop: '1rem' }}>{children}</h4>
    ),
    p: ({ children }: { children: React.ReactNode }) => (
      <p style={{ color: defaultColor, fontFamily, fontSize: '1rem', lineHeight: 1.75, marginBottom: '0.75rem' }}>{children}</p>
    ),
    ul: ({ children }: { children: React.ReactNode }) => (
      <ul style={{ color: defaultColor, fontFamily, fontSize: '1rem', lineHeight: 1.75, paddingLeft: '1.75rem', marginBottom: '0.75rem', listStyleType: 'disc' }}>{children}</ul>
    ),
    ol: ({ children }: { children: React.ReactNode }) => (
      <ol style={{ color: defaultColor, fontFamily, fontSize: '1rem', lineHeight: 1.75, paddingLeft: '1.75rem', marginBottom: '0.75rem', listStyleType: 'decimal' }}>{children}</ol>
    ),
    li: ({ children }: { children: React.ReactNode }) => (
      <li style={{ color: defaultColor, fontFamily, marginBottom: '0.35rem', fontSize: '1rem' }}>{children}</li>
    ),
    strong: ({ children }: { children: React.ReactNode }) => (
      <strong style={{ color: primaryColor, fontWeight: 600 }}>{children}</strong>
    ),
    table: ({ children }: { children: React.ReactNode }) => (
      <div style={{ overflowX: 'auto', marginBottom: '1.25rem', borderRadius: '0.5rem', border: '1px solid #e5e7eb' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.95rem', fontFamily }}>{children}</table>
      </div>
    ),
    thead: ({ children }: { children: React.ReactNode }) => (
      <thead style={{ backgroundColor: `${primaryColor}08` }}>{children}</thead>
    ),
    th: ({ children }: { children: React.ReactNode }) => (
      <th style={{ backgroundColor: `${primaryColor}10`, color: primaryColor, fontWeight: 600, padding: '0.6rem 0.85rem', textAlign: 'left' as const, borderBottom: `2px solid ${primaryColor}30` }}>{children}</th>
    ),
    td: ({ children }: { children: React.ReactNode }) => (
      <td style={{ padding: '0.6rem 0.85rem', borderBottom: '1px solid #e5e7eb', color: defaultColor }}>{children}</td>
    ),
    blockquote: ({ children }: { children: React.ReactNode }) => (
      <blockquote style={{ borderLeft: `4px solid ${primaryColor}`, paddingLeft: '1.25rem', margin: '1rem 0', fontStyle: 'italic', color: '#6b7280', fontSize: '1rem' }}>{children}</blockquote>
    ),
    hr: () => (
      <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '1.5rem 0' }} />
    ),
    a: ({ href, children }: { href?: string; children: React.ReactNode }) => (
      <a href={href} style={{ color: primaryColor, textDecoration: 'underline' }} target={href?.startsWith('http') ? '_blank' : undefined} rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}>{children}</a>
    ),
  };

  // Redirect if no session data
  if (!policyReviewId) {
    return (
      <ScopingAILayout>
        <div className="min-h-screen bg-[#FCFBF4] flex items-center justify-center">
          <div className="max-w-md bg-white rounded-lg border border-gray-200 shadow-sm p-6 text-center">
            <FileText className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <h2 className="text-lg font-semibold text-gray-900 mb-2">No Policy Content Found</h2>
            <p className="text-sm text-gray-500 mb-4">
              Please generate a policy first before editing.
            </p>
            <Button
              onClick={() => navigate('/revisr/create-policy')}
              className="bg-[#33b864] hover:bg-[#2a9d54] text-white"
            >
              Go to Details
            </Button>
          </div>
        </div>
      </ScopingAILayout>
    );
  }

  return (
    <ScopingAILayout>
      <div className="h-[calc(100vh-4rem)] flex flex-col bg-[#FCFBF4]">
        {/* Header - Fixed */}
        <motion.div
          className="flex-shrink-0 bg-white/80 backdrop-blur-xl border-b border-white/20 shadow-sm"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex items-center justify-between mb-3">
              <div>
                <Button
                  variant="ghost"
                  onClick={() => navigate(`/revisr/create-policy/generated?id=${policyReviewId}`)}
                  className="gap-2 hover:bg-white/50 transition-all duration-300 group mb-1 h-8 text-xs"
                >
                  <ArrowLeft className="h-3.5 w-3.5 group-hover:-translate-x-1 transition-transform duration-300" />
                  Back to Generate
                </Button>
                <h1 className="text-xl font-bold text-gray-900">Edit Policy</h1>
                <p className="text-xs text-gray-500 mt-0.5">
                  Review and refine your <strong>{policyName}</strong> sections
                </p>
              </div>

              <div className="flex items-center gap-3">
                {/* Save Status */}
                {saveStatus === 'saving' && (
                  <Badge variant="secondary" className="text-xs">
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    Saving...
                  </Badge>
                )}
                {saveStatus === 'saved' && (
                  <Badge variant="secondary" className="bg-green-50 text-green-700 border-green-200 text-xs">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Saved
                  </Badge>
                )}

                {/* Continue button */}
                <Button
                  onClick={handleContinueToExport}
                  disabled={isSaving || sections.length === 0}
                  className="bg-[#33b864] hover:bg-[#2a9d54] text-white"
                  size="sm"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      Continue to Export
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Stepper */}
            <Stepper currentStep={3} steps={CREATE_POLICY_STEPS} />
          </div>
        </motion.div>

        {/* Main Content - Full remaining height */}
        <div className="flex-1 min-h-0 overflow-hidden">
          <EditorLayout
            className="h-full"
            sidebarWidth="w-72"
            activeTab={activeTab}
            onTabChange={setActiveTab}
            selectedSectionTitle={selectedSection?.title}
            totalSections={sections.length}
            showDocumentPreview={true}
            isLoading={isRegenerating}

            // Action buttons - Regenerate for selected section
            actionButtons={selectedSection && activeTab === 'edit' ? (
              <EditorActionButtons
                leftActions={
                  <span className="text-xs text-gray-500">
                    Section {selectedSectionIndex + 1} of {sections.length}
                  </span>
                }
                rightActions={
                  <Button
                    onClick={handleOpenRegenDialog}
                    variant="outline"
                    size="sm"
                    className="text-xs h-7 gap-1.5"
                    disabled={isRegenerating}
                  >
                    <Sparkles className="h-3 w-3" />
                    Regenerate Section
                  </Button>
                }
              />
            ) : undefined}

            // Section List sidebar
            sectionList={
              <SortableSectionList
                sections={sections}
                selectedSectionId={selectedSectionId}
                onSectionSelect={handleSectionSelect}
                onSectionReorder={handleSectionReorder}
                onSectionTitleChange={handleSectionTitleChange}
                onSectionDelete={handleSectionDelete}
                onSectionAdd={handleSectionAdd}
                onSectionAddAfter={handleSectionAddAfter}
                onSectionEdit={handleSectionEdit}
              />
            }

            // Edit tab - Rich text editor
            editorContent={
              selectedSection ? (
                <div className="h-full flex flex-col">
                  <YooptaMarkdownEditor
                    key={`editor-${selectedSectionId}-${editorKey}`}
                    markdownContent={selectedSection.content}
                    onChange={handleSectionContentChange}
                    placeholder="Start writing section content..."
                    userId={userId}
                    bucketName="policy_media"
                    autoFocus
                    className="flex-1 min-h-0"
                  />
                </div>
              ) : (
                <div className="h-full flex items-center justify-center text-gray-400">
                  <div className="text-center">
                    <FileText className="h-10 w-10 mx-auto mb-3 opacity-30" />
                    <p className="text-sm">Select a section to edit</p>
                  </div>
                </div>
              )
            }

            // Section Preview tab - branded rendering
            previewContent={
              selectedSection ? (
                <div className="p-6">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={brandedMarkdownComponents as Record<string, React.ComponentType>}
                  >
                    {`## ${selectedSection.title}\n\n${selectedSection.content}`}
                  </ReactMarkdown>
                </div>
              ) : (
                <div className="h-full flex items-center justify-center text-gray-400">
                  <div className="text-center">
                    <FileText className="h-10 w-10 mx-auto mb-3 opacity-30" />
                    <p className="text-sm">Select a section to preview</p>
                  </div>
                </div>
              )
            }

            // Document Preview tab - full branded document
            documentPreviewContent={
              sections.length > 0 ? (
                <div className="p-8 max-w-4xl mx-auto">
                  {/* Document header with branding */}
                  {(logoPreview || policyName) && (
                    <div className="mb-6 pb-4 border-b" style={{ borderColor: `${primaryColor}20` }}>
                      {logoPreview && (
                        <img
                          src={logoPreview}
                          alt="Organization Logo"
                          className="h-10 mb-3 object-contain"
                        />
                      )}
                      <h1 style={{ color: primaryColor, fontFamily, fontSize: '1.75rem', fontWeight: 700, marginBottom: '0.25rem' }}>
                        {policyName}
                      </h1>
                      <p style={{ color: '#9ca3af', fontFamily, fontSize: '0.8rem' }}>
                        {currentWorkspace?.name || 'Organization'} &bull; Draft
                      </p>
                    </div>
                  )}

                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={brandedMarkdownComponents as Record<string, React.ComponentType>}
                  >
                    {editorSectionsToMarkdown(sections)}
                  </ReactMarkdown>
                </div>
              ) : (
                <div className="h-full flex items-center justify-center text-gray-400">
                  <div className="text-center">
                    <FileText className="h-10 w-10 mx-auto mb-3 opacity-30" />
                    <p className="text-sm">No content to preview</p>
                  </div>
                </div>
              )
            }
          />
        </div>
      </div>

      {/* Regenerate Dialog */}
      <SectionRegenerateDialog
        open={isRegenerateDialogOpen}
        onOpenChange={setIsRegenerateDialogOpen}
        sectionTitle={regeneratingSectionId ? (sections.find((s) => s.id === regeneratingSectionId)?.title || '') : ''}
        onRegenerate={handleRegenerate}
        isRegenerating={isRegenerating}
      />
    </ScopingAILayout>
  );
}
